// scheduling/utils/schedConstants.js
// Shared constants for the BMSS Scheduler

// ── Brand Colors ────────────────────────────────────────────────────────────
export const BRAND = {
  deepTeal: '#003D4C',
  oceanBlue: '#356371',
  brightGreen: '#6CC24A',
  sageGreen: '#74A282',
};

// ── Job Type Colors (keyed by ServiceDesc from PE) ──────────────────────────
// Each ServiceDesc should have a visually distinct color.
export const JOB_TYPE_COLORS = {
  // AUDIT dept — teal family
  'Audit':                        { bg: '#003D4C', text: '#fff' },  // deep teal
  'Attest Services':              { bg: '#0E7490', text: '#fff' },  // cyan-teal (distinct from Audit)
  // TAX dept — warm/earth family
  'Individual Tax':               { bg: '#B45309', text: '#fff' },  // amber-brown
  'Business Tax':                 { bg: '#15803D', text: '#fff' },  // forest green
  'Not For Profit':               { bg: '#DC2626', text: '#fff' },  // red
  'Estate Trusts Foreign':        { bg: '#BE185D', text: '#fff' },  // rose-pink
  'SALT':                         { bg: '#7C2D12', text: '#fff' },  // burnt orange
  // ADVISORY dept — blue/purple family
  'Valuation & Forensic Services':{ bg: '#6D28D9', text: '#fff' },  // purple
  'Transaction Advisory Services':{ bg: '#4338CA', text: '#fff' },  // indigo
  'Transaction Advisory Services - Tax': { bg: '#5B21B6', text: '#fff' },  // deep purple
  'MAS':                          { bg: '#7E22CE', text: '#fff' },  // violet
  'Human Resource Services':      { bg: '#9333EA', text: '#fff' },  // bright purple
  'EOS Services':                 { bg: '#6366F1', text: '#fff' },  // slate-indigo
  // CAS dept
  'Client Accounting Services':   { bg: '#CA8A04', text: '#fff' },  // gold
  // GOVCON dept
  'Government Contract Consulting':{ bg: '#1D4ED8', text: '#fff' }, // blue
  // AI dept — BMSS AI (00007-101) project jobs. Three of them carry PE's ADMIN service
  // ("Firm - Admin"); no other rprs.Jobs row does, since internal clients don't sync.
  'AI Development':               { bg: '#A21CAF', text: '#fff' },  // fuchsia
  'Firm - Admin':                 { bg: '#A21CAF', text: '#fff' },
  // Legacy keys (from old JobType/JobClassDesc — kept for backwards compat)
  'Tax':                          { bg: '#15803D', text: '#fff' },
  'Advisory':                     { bg: '#4338CA', text: '#fff' },
  'Review':                       { bg: '#0E7490', text: '#fff' },
  'Compliance':                   { bg: '#15803D', text: '#fff' },
  'Bookkeeping':                  { bg: '#CA8A04', text: '#fff' },
  'Payroll':                      { bg: '#CA8A04', text: '#fff' },
  '1040':                         { bg: '#B45309', text: '#fff' },
  '1065':                         { bg: '#BE185D', text: '#fff' },
  '1120S':                        { bg: '#EA580C', text: '#fff' },
  '1120':                         { bg: '#15803D', text: '#fff' },
  '990':                          { bg: '#DC2626', text: '#fff' },
};

// ── Custom job category colors (Item 2) ───────────────────────────────────
// Category does double duty: it is this colour key AND — since Trello #309 — the
// Tax/Assurance classification the Gen Track balance report reads. 'Tax' and
// 'Assurance' count toward the balance; 'Administrative' and 'Other' don't.
//
// Tax and Assurance reuse the EXACT DEPT_GROUPS hues, so "green = tax, teal =
// assurance" reads the same on a custom block as on real client work. The
// non-service categories stay in the muted slate family, so internal overhead still
// reads as overhead against the vivid JOB_TYPE_COLORS palette above.
//
// Sharing a hue with real client work is safe because it is NOT the only signal: the
// .sched-grid-block--custom stripe overlay + ✦ glyph mark every custom block
// regardless of category, and those are keyed on isCustom, not on colour.
export const CUSTOM_JOB_CATEGORY_COLORS = {
  'Tax':            { bg: '#74A282', text: '#fff' },  // = DEPT_GROUPS TAX
  'Assurance':      { bg: '#356371', text: '#fff' },  // = DEPT_GROUPS AUDIT
  'Administrative': { bg: '#475569', text: '#fff' },  // slate-600
  'Other':          { bg: '#64748B', text: '#fff' },  // slate-500
};
export const CUSTOM_JOB_DEFAULT_COLOR = { bg: '#6B7280', text: '#fff' };  // gray-500 fallback

// The four allowed Category values, senior-most first so every <select> renders them
// in the same order. Mirrors CK_CustomJobs_Category (migrate-072) and the server-side
// CUSTOM_JOB_CATEGORIES guard in api/schedWrite.
export const CUSTOM_JOB_CATEGORIES = ['Tax', 'Assurance', 'Administrative', 'Other'];

// ── Gantt engagement type colors ────────────────────────────────────────────
export const ENTRY_TYPE_COLORS = {
  billable: { bg: '#356371', text: '#fff' },
  audit:    { bg: '#356371', text: '#fff' },
  tax:      { bg: '#74A282', text: '#fff' },
  pto:      { bg: '#9CA3AF', text: '#fff' },
  training: { bg: '#D97706', text: '#fff' },
  admin:    { bg: '#7C3AED', text: '#fff' },
};

// ── Department group colors ─────────────────────────────────────────────────
export const DEPT_GROUPS = [
  { id: 'AUDIT',    label: 'Audit & Assurance',  color: '#356371' },
  { id: 'TAX',      label: 'Tax',                color: '#74A282' },
  { id: 'CAS',      label: 'CAS',                color: '#D97706' },
  { id: 'ADVISORY', label: 'Advisory',            color: '#0891B2' },
  { id: 'GOVCON',   label: 'GovCon',             color: '#4338CA' },
  { id: 'ESTATE',   label: 'Estate',             color: '#E11D48' },
  { id: 'GENERAL',  label: 'General',            color: '#7C3AED' },
  { id: 'STEERING', label: 'Steering',           color: '#003D4C' },
  { id: 'ADMIN',    label: 'Admin',              color: '#6B7280' },
  { id: 'OTHER',    label: 'Other',              color: '#9CA3AF' },
  // jobOnly: a job grouping with no staff of its own (nobody's home department), so it
  // is a Jobs-view tab only and never a staff-department option — use STAFF_DEPT_GROUPS there.
  { id: 'AI',       label: 'AI',                 color: '#A21CAF', jobOnly: true },
];

// DEPT_GROUPS minus the job-only groupings: every picker/filter over STAFF departments.
export const STAFF_DEPT_GROUPS = DEPT_GROUPS.filter(g => !g.jobOnly);

// ── Staff color: department hue + seniority shade ───────────────────────────
// One rule, used wherever a STAFF identity swatch renders (avatars/bars in People
// view, staff blocks + detail avatars in Jobs view): hue = department, shade = level
// (darker = more senior). Jobs reuse DEPT_GROUPS for their (service-implied) dept too,
// so a staff swatch matching a job's dept bar means that staffer is in the job's dept.

// Raw PE dept codes are messier than the 9 groups — normalize onto a DEPT_GROUPS id.
export const DEPT_NORMALIZE = {
  CONSULT: 'ADVISORY',
  ADMINGA: 'ADMIN', ADMINOP: 'ADMIN', ADMINHR: 'ADMIN', ADMINAD: 'ADMIN', ADMINRN: 'ADMIN',
  OTHER: 'OTHER', UNKNOWN: 'OTHER', XGEN: 'OTHER',
};
export function normalizeDept(code) {
  if (!code) return 'OTHER';
  const c = String(code).toUpperCase().trim();
  if (DEPT_NORMALIZE[c]) return DEPT_NORMALIZE[c];
  return DEPT_GROUPS.some(g => g.id === c) ? c : 'OTHER';
}

export function deptColorById(deptId) {
  return DEPT_GROUPS.find(g => g.id === deptId)?.color || '#9CA3AF';
}

// Darken a #rrggbb hex toward black by t (0–1).
export function darkenHex(hex, t) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const f = c => Math.round(c * (1 - t)).toString(16).padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`;
}

// Seniority band 0–3 (higher = more senior). Band 3 mirrors the leadership set used
// by the grid's hideLeadership filter.
export function LEVEL_BAND(level) {
  const l = level || '';
  if (l === 'Member' || l === 'Principal' || l === 'Senior Manager') return 3;
  if (l === 'Manager' || l === 'Supervisor') return 2;
  if (l === 'Senior Accountant') return 1;
  return 0; // Staff A/B, Intern, Consultant, unknown
}
// Legend ramp labels (band index → display label + the PE levels that fall in it).
export const LEVEL_BANDS = [
  { band: 0, label: 'Staff',      levels: ['Intern', 'Staff A', 'Staff B', 'Consultant'] },
  { band: 1, label: 'Senior',     levels: ['Senior Accountant'] },
  { band: 2, label: 'Manager',    levels: ['Manager', 'Supervisor'] },
  { band: 3, label: 'Leadership', levels: ['Senior Manager', 'Principal', 'Member'] },
];

// Staff identity swatch hue = pure department color, regardless of level. Seniority is
// carried separately by the SeniorityBars rank glyph (the old darker-shade-by-level
// scheme packed 4 bands into one hue and they were too close to distinguish). LEVEL_BAND
// above still drives that glyph; darkenHex stays exported for other callers.
export function staffColor(deptCode) {
  return deptColorById(normalizeDept(deptCode));
}

// ── Return type (1040 / 1120S / 1065 …) ────────────────────────────────────
// Suggestion #17 (kfluker): filter the grid by return type. PE has no field for
// this — every candidate column is a whole order of magnitude too coarse:
//   JobType / JobClassDesc → 2 values  (Compliance 8,344 · Advisory 86)
//   ServiceIndex / ServiceDesc → 7 values (INDTAX, BUSTAX, ESTATE, AUDIT, …)
// The form number only ever appears inside the job NAME, which PE generates from a
// job template ("2026 1120S S-Corporation Return"), so it is structured enough to
// match on even though it is nominally free text.
//
// ORDER MATTERS: 1120S must be tested before 1120, or every S-corp return is
// misfiled as a C-corp. Keep the most specific pattern first.
//
// Counts over the 8,499 live jobs at time of writing: 1040 3,152 · 1065 1,606 ·
// 1120S 913 · 1041 866 · 1120 345 · 990 289 · 709 260 · 5500 52 · other 1,016.
// The "other" bucket is entirely audit/attest work (Audited/Compiled/Reviewed
// Financial Statement, EBP Audit, Business Valuation) — hence the explicit
// non-return option, so "everything that isn't a return" stays selectable rather
// than being silently unreachable.
export const RETURN_TYPES = [
  { id: '1120S', label: '1120S', match: '1120s' },
  { id: '1120',  label: '1120',  match: '1120'  },
  { id: '1065',  label: '1065',  match: '1065'  },
  { id: '1040',  label: '1040',  match: '1040'  },
  { id: '1041',  label: '1041',  match: '1041'  },
  { id: '990',   label: '990',   match: '990'   },
  { id: '709',   label: '709',   match: '709'   },
  { id: '5500',  label: '5500',  match: '5500'  },
];

// Sentinel for "has no form number" — audits, financial statements, valuations.
export const RETURN_TYPE_OTHER = 'other';

/**
 * Classify a job by the form number in its name.
 * @param {string} jobName  e.g. "2026 1120S S-Corporation Return"
 * @returns {string} a RETURN_TYPES id, or RETURN_TYPE_OTHER
 */
export function getReturnType(jobName) {
  const n = (jobName || '').toLowerCase();
  if (!n) return RETURN_TYPE_OTHER;
  for (const t of RETURN_TYPES) {
    if (n.includes(t.match)) return t.id;
  }
  return RETURN_TYPE_OTHER;
}

// ── Service → Department mapping (from PE tblServices → tblDepartment) ──────
// Used to resolve a job's department from its ServiceDesc for filtering + color coding.
export const SERVICE_TO_DEPT = {
  'Audit':                              'AUDIT',
  'Attest Services':                    'AUDIT',
  'Business Tax':                       'TAX',
  'Individual Tax':                     'TAX',
  'Not For Profit':                     'TAX',
  'SALT':                               'TAX',
  'Estate Trusts Foreign':              'ESTATE',
  'Client Accounting Services':         'CAS',
  'Government Contract Consulting':     'GOVCON',
  'Human Resource Services':            'ADVISORY',
  'MAS':                                'ADVISORY',
  'Transaction Advisory Services':      'ADVISORY',
  'Transaction Advisory Services - Tax':'ADVISORY',
  'Valuation & Forensic Services':      'ADVISORY',
  'EOS Services':                       'ADVISORY',
  'AI Development':                     'AI',
  'Firm - Admin':                       'AI',   // only BMSS AI jobs carry it in rprs.Jobs (see JOB_TYPE_COLORS)
};

// BMSS AI — the one INTERNAL PE client whose jobs sync (suggestion #26): firm AI projects.
// They are ordinary schedulable jobs; the grid modal's "AI" picker filters to them.
export const AI_CLIENT_CODE = '00007-101';
export function isAiJob(job) {
  return !!job && job.ClientCode === AI_CLIENT_CODE;
}

// Helper: resolve department from a job's ServiceDesc
export function jobDept(serviceDesc) {
  return SERVICE_TO_DEPT[serviceDesc] || null;
}

// Helper: get department color for a job
export function jobDeptColor(serviceDesc) {
  const dept = SERVICE_TO_DEPT[serviceDesc];
  if (!dept) return '#6B7280'; // gray for unknown
  return DEPT_GROUPS.find(g => g.id === dept)?.color || '#6B7280';
}

// ── Staff-suggestion base filters ───────────────────────────────────────────
// The Staff Suggestions page and the grid modal's quick-suggest rank the SAME
// backend candidate list; these are the auto-filters that keep the two views in
// agreement. Leadership is hidden by default; the page can opt back in via a
// toggle, the modal always hides it.
//
// LEADERSHIP_LEVELS is the canonical "hide leadership / Mgr & below" FILTER set,
// shared with the grid People-view toggle + placeholder-row eligibility. Consultant
// is a senior advisory role, so it's treated as leadership for these filters
// (hidden by default, shown when leadership is included). NOTE: this is the FILTER
// set only — the seniority rank glyph + sort (LEVEL_BAND / LEVEL_RANK) still place
// Consultant at band 0. (Member / Principal / Senior Manager are the leadership
// titles; Principal and Senior Manager are the same title.)
export const LEADERSHIP_LEVELS = ['Member', 'Principal', 'Senior Manager', 'Consultant'];
export const EXCLUDED_SUGGESTION_LEVELS = new Set(LEADERSHIP_LEVELS);

// Ordered options for the Staff Suggestions level picklist (senior → junior).
// Non-leadership are always selectable; the leadership set (incl. Consultant) is
// only offered when the "include leadership" toggle is on.
export const SUGGESTION_LEVEL_OPTIONS = ['Manager', 'Supervisor', 'Senior Accountant', 'Staff B', 'Staff A', 'Intern'];

// Every level a placeholder can be parked on, most junior first. Shared by the grid's
// new-placeholder pickers and PlaceholderAssignModal's edit section: two lists that
// must agree, because editing a placeholder is how you MOVE it between these, and an
// option missing from one side would be a level you could create but never leave.
// Broader than SUGGESTION_LEVEL_OPTIONS on purpose — leadership can hold a placeholder
// even though they are excluded from ranked suggestions.
export const PLACEHOLDER_LEVELS = [
  'Intern', 'Consultant', 'Staff A', 'Staff B', 'Senior Accountant',
  'Supervisor', 'Manager', 'Senior Manager', 'Principal', 'Member',
];
export const SUGGESTION_LEADERSHIP_LEVELS = LEADERSHIP_LEVELS;

// Levels that are NOT schedulable on client work — admin/ops staff, unmapped, and
// the "No Selection" placeholder (e.g. the "Scan Verify" automation pseudo-staffer).
// These people can still LOG WIP (overhead/automation time), so they surface in a
// job's WIP contributors, but they must NOT appear as pickable candidates in the
// Schedule chooser / suggestions.
export const NON_SCHEDULABLE_LEVELS = new Set([
  'Administrative', 'Admin Management', 'No Selection', 'Unknown', 'Former Employee',
]);
export function isSchedulableStaff(level) {
  return !!level && !NON_SCHEDULABLE_LEVELS.has(level);
}

// Department allow-set for a job's ServiceDesc. Audit jobs draw from AUDIT+GENERAL,
// tax/estate from TAX+ESTATE+GENERAL, etc. Returns null when the service has no
// mapping (→ no department restriction).
export function allowedDeptsForService(serviceDesc) {
  const dept = SERVICE_TO_DEPT[serviceDesc];
  if (!dept) return null;
  if (dept === 'AUDIT') return new Set(['AUDIT', 'GENERAL']);
  if (dept === 'TAX' || dept === 'ESTATE') return new Set(['TAX', 'ESTATE', 'GENERAL']);
  if (dept === 'ADVISORY') return new Set(['ADVISORY', 'CONSULT', 'GENERAL']);
  if (dept === 'GOVCON') return new Set(['GOVCON', 'GENERAL']);
  if (dept === 'CAS') return new Set(['CAS', 'GENERAL']);
  return null;
}

// Apply the default suggestion auto-filters: restrict to the job's department
// allow-set (if any) and drop leadership levels unless includeLeadership is set.
// Shared by StaffSuggestions (page) and the grid modal's quick-suggest so their
// rankings stay identical for a given job.
export function baseSuggestionFilter(candidates, serviceDesc, { includeLeadership = false, onSiteOfficeCode = null } = {}) {
  const allowed = allowedDeptsForService(serviceDesc);
  return (candidates || []).filter(c => {
    if (!includeLeadership && EXCLUDED_SUGGESTION_LEVELS.has(c.Level)) return false;
    if (allowed && !allowed.has(c.DepartmentCode)) return false;
    // On-site job: only staff from the job's office (OfficeCode exact match).
    if (onSiteOfficeCode && c.OfficeCode !== onSiteOfficeCode) return false;
    return true;
  });
}

// ── Schedule status badges ──────────────────────────────────────────────────
export const SCHED_STATUS = {
  unscheduled:   { label: 'Unscheduled',         bg: '#FEF3C7', text: '#B45309', dot: '#F59E0B' },
  partial:       { label: 'Partially Scheduled', bg: '#DBEAFE', text: '#1D4ED8', dot: '#3B82F6' },
  scheduled:     { label: 'Scheduled',           bg: '#D1FAE5', text: '#065F46', dot: '#10B981' },
  overbudget:    { label: 'Over Budget',         bg: '#FEE2E2', text: '#DC2626', dot: '#DC2626' },
  overscheduled: { label: 'Over-Scheduled',      bg: '#FFF7ED', text: '#C2410C', dot: '#EA580C' },
  nobudget:      { label: 'No Budget',           bg: '#F3F4F6', text: '#4B5563', dot: '#9CA3AF' },
};

// Compute a job's schedule status from budget, scheduled (today-onward) hours, and
// WIP (logged) hours. Returned key maps to SCHED_STATUS. Shared by ManagerDashboard
// + the Jobs-view grid chips.
// No budget = nothing to measure against, so it gets its own status rather than a
// permanent Unscheduled / Partial (about a quarter of open jobs, incl. every YETP
// Tax Planning job, whose PE template carries no budget).
export function computeSchedStatus(budgetHrs, scheduledHrs, wipHrs) {
  if (!budgetHrs || budgetHrs <= 0) return 'nobudget';
  const totalUsed = (wipHrs || 0) + scheduledHrs;
  if (wipHrs > budgetHrs) return 'overbudget';        // WIP alone exceeds budget
  if (totalUsed > budgetHrs) return 'overscheduled';   // WIP + scheduled exceeds budget
  if (scheduledHrs <= 0) return 'unscheduled';
  if (totalUsed >= budgetHrs) return 'scheduled';
  return 'partial';
}

// ── Job status badges ───────────────────────────────────────────────────────
export const JOB_STATUS = {
  'Not Started':  { bg: '#F3F4F6', color: '#6B7280' },
  'In Progress':  { bg: '#DBEAFE', color: '#1D4ED8' },
  'Scheduled':    { bg: '#D1FAE5', color: '#065F46' },
  'Complete':     { bg: '#D1FAE5', color: '#065F46' },
  'Waiting':      { bg: '#FEF3C7', color: '#B45309' },
};

// ── Alert severity colors ───────────────────────────────────────────────────
export const SEVERITY = {
  high:   '#EF4444',
  medium: '#F59E0B',
  low:    '#3B82F6',
};

// ── Experience scoring (percentile-based bell curve) ────────────────────────
export const EXP_WEIGHTS = { client: 3, niche: 2, serviceline: 1 };
export const EXP_MAX_SCORE = 100; // normalized 0-100 fit score

// Percentile-based levels (replaces flat 100h/pt system)
export const EXP_LEVELS = [
  { min: 0,  max: 2,   level: 0, label: '\u2014',        color: '#E5E7EB' },
  { min: 3,  max: 15,  level: 1, label: 'Emerging',    color: '#9CA3AF' },
  { min: 16, max: 40,  level: 2, label: 'Developing',  color: '#F59E0B' },
  { min: 41, max: 70,  level: 3, label: 'Proficient',  color: '#74A282' },
  { min: 71, max: 90,  level: 4, label: 'Advanced',    color: '#356371' },
  { min: 91, max: 100, level: 5, label: 'Expert',      color: '#003D4C' },
];

// Map flat level (old 100h/pt system) to midpoint percentile for small populations
export const FLAT_TO_PERCENTILE = [1, 9, 28, 55, 80, 95];

// Get EXP_LEVELS entry for a percentile value
export function getExpLevel(percentile) {
  if (percentile == null) return EXP_LEVELS[0];
  for (let i = EXP_LEVELS.length - 1; i >= 0; i--) {
    if (percentile >= EXP_LEVELS[i].min) return EXP_LEVELS[i];
  }
  return EXP_LEVELS[0];
}

// ── PI Reference Profiles (17 types from Predictive Index) ──────────────────
export const PI_PROFILES = {
  Adapter:       { category: 'Stabilizing', desc: 'Flexible, accommodating. Adapts easily to change and focuses on maintaining harmony.' },
  Altruist:      { category: 'Social',      desc: 'Caring, service-oriented. Motivated by helping others and contributing to the greater good.' },
  Analyzer:      { category: 'Analytical',  desc: 'Data-driven, detail-oriented. Values accuracy, thorough analysis, and logical decisions.' },
  Artisan:       { category: 'Persistent',  desc: 'Creative, independent worker. Values flexibility and self-expression. Innovative problem-solver.' },
  Captain:       { category: 'Persistent',  desc: 'Strong-willed, competitive leader. Driven by achievement and control. Commanding presence.' },
  Collaborator:  { category: 'Social',      desc: 'Team-focused, harmonious. Values relationships and group success over individual achievement.' },
  Controller:    { category: 'Analytical',  desc: 'Precise, methodical. Values order and control. Prefers structure and clear systems.' },
  Guardian:      { category: 'Stabilizing', desc: 'Reliable, loyal. Values stability and dedicated service. Strong follow-through.' },
  Individualist: { category: 'Persistent',  desc: 'Independent, analytical thinker. Values objectivity and logic. Prefers data over consensus.' },
  Maverick:      { category: 'Persistent',  desc: 'Bold, unconventional risk-taker. Challenges the status quo and pushes boundaries.' },
  Operator:      { category: 'Stabilizing', desc: 'Practical, action-oriented. Gets things done. Values results and tangible outcomes.' },
  Persuader:     { category: 'Social',      desc: 'Charismatic communicator. Influences through ideas and reasoning. Values persuasion.' },
  Promoter:      { category: 'Social',      desc: 'Enthusiastic, energetic. Generates excitement and brings infectious optimism. Team energizer.' },
  Scholar:       { category: 'Stabilizing', desc: 'Thoughtful, analytical learner. Values knowledge and understanding. Continuous learner.' },
  Specialist:    { category: 'Analytical',  desc: 'Focused, detailed professional. Specializes in specific domains. Values deep expertise.' },
  Strategist:    { category: 'Analytical',  desc: 'Big-picture, long-term thinker. Sees connections and possibilities. Pattern recognition.' },
  Venturer:      { category: 'Analytical',  desc: 'Adventurous, opportunity-seeking. Thrives on novelty and change. Embraces uncertainty.' },
};

export const PI_CATEGORY_COLORS = {
  Analytical:  { bg: '#EFF6FF', text: '#1D4ED8' },
  Social:      { bg: '#FEF3C7', text: '#92400E' },
  Persistent:  { bg: '#F0FDF4', text: '#166534' },
  Stabilizing: { bg: '#F5F3FF', text: '#5B21B6' },
};

// ── PE Job Work Status → Phase Mapping ─────────────────────────────────────
// Phase 0 = Not Started, Phase 1 = Prep, Phase 2 = Review, Phase 3 = Member Review, Phase 4 = Finalization
// Source: scheduling_brainstorm/emp_recs/milestone mapping.csv (178 statuses)
const PHASE_0_STATUSES = new Set([
  'Holding to E-File - Missing Signed MSEL or SOW',
  'SOW Approve', 'SOW Assurance Preparation', 'SOW Assurance Review',
  'SOW Preparation', 'SOW Preparation (Custom)', 'SOW Send To Client',
  'SOW Send To Client (Mail)', 'SOW Sent - No Info In',
  'SOW Tax Preparation', 'SOW Tax Review',
]);
const PHASE_1_STATUSES = new Set([
  '1040 Scanning', '1040 Stand Alone - Need Billing Amount',
  'AF Binder Issues', 'AF Binder Issues - Corrected',
  'Admin - TC Organize', 'Admin - To Be Scanned',
  'Batch Extended', 'Billing Prepare', 'Billing Review',
  'Child of Signer Group (No action Required)',
  'Client Meeting', 'DS - To Be Sorted', 'Documents Scanned',
  'Draft Follow-Up Letter', 'ECOE - Mail Welcome Packet',
  'ECOE - Print Welcome Packet', 'ECOE - Sign Welcome Packet',
  'EXT Complete - Awaiting Info', 'EXT Efiled Awaiting Acceptance',
  'EXT Efiled to Be Transmitted', 'EXT Not In Process',
  'EXT Prepare', 'EXT Review', 'EXT Second Review',
  'Engagement Leader Contact', 'Engagement Letter Needed',
  'Fieldwork', 'Filing Late', 'Finalize Binder',
  'Follow Up With Client - One Year', 'Hold to Post 4/15',
  'Holding to E-File - Return Needs Export', 'In Progress',
  'Letter Sent', 'Letter to be Mailed',
  'MSEL Paper Sent by Admin Team', 'MSEL Sent by Admin Team',
  'MSEL Signature Declined', 'Meeting Booked', 'Member Contact',
  'New Client - Acceptance Review', 'New Client - Setup',
  'New Client EL Review', 'New Client Fill Out Forms',
  'New Client Final Review', 'New Client Marketing', 'New Status',
  'No Info In EL', 'Not Doing CY', 'Not Started',
  'Offshore Group Assignment', 'Offshore Validation',
  'Offshore Validation Failed', 'Open Proofing Points',
  'Part of Group MSEL - No Action Required',
  'Preparation', 'Preparation - Offshore Preparer',
  'Preparation - ScanVerify', 'Preparation - SurePrep Domestic Outsource',
  'Prepare 7216', 'Preparer Date FS',
  'Proofing (if full report)', 'Proofing Assign (if full report)',
  'Proposal - Committee Review', 'Proposal - Delivery',
  'Proposal - Prepare', 'Proposal - Review', 'Proposal - Status Update',
  'Proposal Assembly', 'Proposal Clear Review Notes',
  'Proposal Final Budget', 'Proposal Proofread',
  'QC Final (if full report)', 'Questions Answered', 'Questions Posted',
  'Ready for 1040 Prep', 'Ready for Accrual', 'Report Preparation',
  'Reviewed - Waiting on Outstanding Items', 'SBA Loan Assistance',
  'SP Binder Creation', 'SP Binder Creation - Offshore Preparer',
  'SP Binder Creation - SurePrep Domestic Outsource',
  'SP Binder Rejected', 'SP Outsource Cleanup',
  'Schedule Client Meeting', 'Shareholder - Final', 'Shareholder - Initial',
  'Signed E-File Forms Received', 'Signed Off',
  'Sponsorship Committee Review', 'Sponsorship Info Recd',
  'Sponsorship Prepare', 'Sponsorship Review', 'Sponsorship To Be Paid',
  'Sponsorships QB Entry', 'To Be Approved',
  'To Be Assembled (if full report)', 'To Be Billed',
  'To Be Marked Completed', 'To Be Processed',
  'To Be Scanned and Uploaded', 'Unassigned', 'Verify Project List',
  'Waiting For Client', 'Waiting on AR Collection',
  'Waiting on Board Approval', 'Waiting on Form 5500',
  'Waiting on Information from Client', 'Waiting on Outstanding Items',
  'Waiting on Signed 7216', 'Waiting on Signed Engagement Letter',
  'Waiting on Updated Forms', 'Waiting on Worksheets - AD VALOREM',
  'Waiting on YE Adjustments', 'Waiting on YE Write-up',
  'Worksheets to Be Sent - AD VALOREM',
]);
const PHASE_2_STATUSES = new Set([
  'Cleared Report Review Points', 'Cleared Review Points',
  'Detail Review', 'Final Review', 'Open Report Review Points',
  'Open Review Points', 'Proofing', 'Proofing Assign',
  'Report Review', 'Review', 'Review - Final', 'Review / Hold',
  'Unassigned - Review',
]);
const PHASE_3_STATUSES = new Set([
  'Awaiting Client Approval', 'Awaiting Client Info',
  'Cleared Member Notes', 'Cleared Member Review Notes',
  'Cleared QC Points', 'Member Approve Draft',
  'Member Review - Final', 'Member Review - Initial',
  'Open Member Points', 'Open Member Review Notes', 'Open QC Points',
  'Proofing - Assign', 'QC Assign', 'QC Review', 'QC Review - Final',
  'QC Review - Second', 'QC Review Assign', 'Quality Control - Assign',
  'Quality Control - Final', 'Quality Control - Initial',
  'Quality Control - Second', 'Quality Control Review',
  'Shareholder Review', 'Signer Review',
]);
const PHASE_4_STATUSES = new Set([
  'Assembly Review (HSV)', 'Completed', 'E-File Rejected',
  'E-File Rejection Corrected', 'Efiled', 'Tax Return Finalized (HSV)',
  'To Be Assembled', 'To Be Checked', 'To Be Finalized',
  'To Be Shipped', 'To Be eFiled', 'To be eFiled Waiting on Sig Forms',
  'Unassigned To Be Assembled',
]);

export const PHASE_LABELS = ['Not Started', 'Prep', 'Review', 'Member Review', 'Finalization'];
export const PHASE_COLORS = ['#9CA3AF', '#3B82F6', '#F59E0B', '#8B5CF6', '#10B981'];

export function getJobPhase(statusDetail) {
  if (!statusDetail) return -1;
  if (PHASE_0_STATUSES.has(statusDetail)) return 0;
  if (PHASE_1_STATUSES.has(statusDetail)) return 1;
  if (PHASE_2_STATUSES.has(statusDetail)) return 2;
  if (PHASE_3_STATUSES.has(statusDetail)) return 3;
  if (PHASE_4_STATUSES.has(statusDetail)) return 4;
  return -1; // unknown status not in mapping
}

// Phase at or past which a job counts as "past review" for the hide-review-stage
// filters (My Jobs + My Backlog). Phase 3 = Member Review, 4 = Finalization.
export const REVIEW_CUTOFF_PHASE = 3;

// True when a job's PE work status is at/after Member Review. Conservative: an
// unknown status (getJobPhase -> -1) or anything in Not Started/Prep/Review
// (phases 0-2) is NOT post-review, so it is never hidden by the review filters.
export function isPostReview(statusDetail) {
  return getJobPhase(statusDetail) >= REVIEW_CUTOFF_PHASE;
}

// ── "Jobs I lead" (JM/JP) membership ─────────────────────────────────────────
// Single source of truth for the Job-Manager-OR-Job-Partner rule. Previously this
// exact condition was re-implemented independently in UnifiedDashboard (badge),
// ManagerDashboard (My Jobs table filter + myJobs memo) and StaffDashboard's server
// query. `staffId` is the viewer's StaffId; there is no stored JM/JP boolean — it's
// derived from the job's ManagerId / PartnerId.
export function isLeadOf(job, staffId) {
  return staffId != null && (job.ManagerId === staffId || job.PartnerId === staffId);
}

// ── My Jobs backlog membership ───────────────────────────────────────────────
// Single source of truth for "does this job belong on my (JM/JP) jobs backlog?" —
// used by both the backlog list (StaffDashboard) and the tab badge
// (UnifiedDashboard) so the two never drift. This is the JM/JP, whole-job lens
// (the caller scopes to jobs you lead); it surfaces led jobs that need scheduling.
// Personal time-entry work is now the separate "My Time" backlog (isMyTimeJob), so
// this no longer requires YOU specifically to have logged hours. A job qualifies
// when ALL hold:
//   • it has a budget (>0h)
//   • the TEAM has logged >5h on it (real work started) → wip.totalHours
//   • >10h of budget is still UNSCHEDULED               → budget − team WIP − future scheduled
//   • it's in an active phase — past kickoff (phase 0) and not finalization (phase 4)
//   • (optional) with hidePostReview on, it's not at/after Member Review
// `wip` is the per-job aggregate { myHours, totalHours }; `scheduledHours` is the
// job's future-scheduled total (0 when the caller has no schedule data).
export function isBacklogJob(job, wip, { hidePostReview = false, scheduledHours = 0 } = {}) {
  // Pooled jobs are out of the backlog by definition (Job Pools, Trello #257):
  // the backlog answers "what still needs scheduling?", and a job sitting in a
  // pool is deliberately NOT going to be scheduled — an eligible staffer will
  // claim it. Checked here rather than in the selectors so the list and the tab
  // badge can't disagree.
  if (job.PoolJobId) return false;
  const budget = job.BudgetHours || 0;
  if (budget <= 0) return false;
  if (!wip || (wip.totalHours || 0) < 5) return false;
  if (budget - wip.totalHours - scheduledHours <= 10) return false;
  const phase = getJobPhase(job.StatusDetail);
  if (phase === 0 || phase === 4) return false;
  if (hidePostReview && isPostReview(job.StatusDetail)) return false;
  return true;
}

// ── My Time membership ──────────────────────────────────────────────────────
// Single source of truth for "does this job belong on my personal (time-entry)
// backlog?" — used by the My Time list (StaffDashboard) and the tab badge
// (UnifiedDashboard). This is the EVERYONE-scoped, WIP-driven companion to
// isBacklogJob (which is JM/JP-scoped). A job qualifies when ALL hold:
//   • it has a budget (>0h)
//   • I logged time on it (my WIP > 0)                → wip.myHours
//     (recency is applied upstream via queryJobsWIP's recentDays filter +
//      the caller's client-side LastDate narrowing, so any wip passed here is
//      already within the window)
//   • the job still has budget REMAINING (>0)         → budget − all-staff WIP − future scheduled
//   • it's in an active phase — past kickoff (phase 0) and not finalization (phase 4)
//   • I have NO upcoming scheduled time on it          → hasUpcomingSched
// `wip` is the per-job aggregate for THIS staffer's row from a withJobTotals
// query: { myHours, jobWipTotal, jobFutureSched }.
export function isMyTimeJob(job, wip, { hasUpcomingSched = false } = {}) {
  // Same reasoning as isBacklogJob: a pooled job isn't waiting on you to schedule
  // it, even if you've logged time on it before.
  if (job.PoolJobId) return false;
  const budget = job.BudgetHours || 0;
  if (budget <= 0) return false;
  if (!wip || (wip.myHours || 0) <= 0) return false;
  if (budget - (wip.jobWipTotal || 0) - (wip.jobFutureSched || 0) <= 0) return false;
  const phase = getJobPhase(job.StatusDetail);
  if (phase === 0 || phase === 4) return false;
  if (hasUpcomingSched) return false;
  return true;
}

// ── Gantt layout constants ──────────────────────────────────────────────────
export const GANTT = {
  DAY_WIDTH: 98,
  ROW_HEIGHT: 46,
  LABEL_WIDTH: 195,
};